/**
 * <zh-speed-dial> — floating quick-access menu (sections/zh-speed-dial.liquid).
 *
 * Defined once per page as a custom element: the theme editor replaces section markup wholesale and
 * inline scripts do not run again, but a registered custom element upgrades the fresh nodes on its
 * own. All visual state lives in CSS, driven by data-open on the .zhsd root.
 */
if (!customElements.get('zh-speed-dial')) {
  // How long an action waits for a widget that is still loading, and how often it looks.
  // Fifteen seconds rather than the five this started with: five covers a warm cache and a
  // desk connection, and Smile on a cold mobile start is the case that misses it. Past this
  // the widget is not late, it is broken or blocked, and a panel that opens on its own that
  // long after the tap is worse than none.
  // zh-chat-loader.js mirrors this budget for the customer portal CTAs.
  const ACTION_RETRY_STEP_MS = 100;
  const ACTION_RETRY_LIMIT = 150;

  class ZhSpeedDial extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('.zhsd');
      this.toggle = this.querySelector('.zhsd__toggle');
      this.list = this.querySelector('.zhsd__list');
      this.backdrop = this.querySelector('.zhsd__backdrop');
      this.status = this.querySelector('.zhsd__status');
      this.links = Array.from(this.querySelectorAll('.zhsd__link'));

      if (!this.root || !this.toggle) return;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.closeOutside = this.dataset.closeOutside !== 'false';
      this.closeAfterClick = this.dataset.closeAfterClick !== 'false';
      this.useMagnet = this.dataset.magnet !== 'false';
      this.useRipple = this.dataset.ripple !== 'false';

      // connectedCallback runs again whenever the element is re-inserted, so the listeners on our
      // own nodes are attached exactly once. Attaching them twice would make one click toggle twice.
      if (!this.bound) {
        this.bindOwnNodes();
        this.bound = true;
      }

      this.scheduleArrival();
      this.observePurchaseControls();
      this.observeBuyBar();
      this.observeHeroBuy();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        document.addEventListener('shopify:section:load', this.onEditorSectionLoad);
      }
    }

    /**
     * The theme editor re-renders a section on every edit and swaps in new nodes, the sticky bar
     * and the hero's buy row included. The observers above stayed on the detached old nodes, so
     * in the editor the bar came up over a menu that never stepped aside (user, 2026-10-13, Doug
     * page). Re-attach them to whatever is in the document now.
     */
    onEditorSectionLoad() {
      this.observePurchaseControls();
      this.observeBuyBar();
      this.observeHeroBuy();
    }

    disconnectedCallback() {
      if (!this.bound) return;

      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:load', this.onEditorSectionLoad);
      if (this.purchaseObserver) this.purchaseObserver.disconnect();
      if (this.buyBarObserver) this.buyBarObserver.disconnect();
      if (this.heroBuyObserver) this.heroBuyObserver.disconnect();
      this.cancelPendingAction();
    }

    observePurchaseControls() {
      if (!document.body || document.body.dataset.template !== 'product' || typeof window.IntersectionObserver !== 'function') return;

      const purchaseControls = document.querySelector('.buy-buttons');
      if (!purchaseControls) return;

      if (this.purchaseObserver) this.purchaseObserver.disconnect();
      this.purchaseObserver = new window.IntersectionObserver((entries) => {
        const purchaseControlsVisible = entries.some((entry) => entry.isIntersecting);
        const isMobile = window.matchMedia('(max-width: 699px)').matches;
        this.root.classList.toggle('zhsd--purchase-visible', isMobile && purchaseControlsVisible);
      });
      this.purchaseObserver.observe(purchaseControls);
    }

    /**
     * Sticky buy bar (<product-quick-add>): while it is up, this menu steps aside at every
     * width. The bar owns the bottom edge including this corner, so the toggle would sit on
     * the buy button, and the button is what the page is for.
     *
     * Watched as a class mutation rather than with an IntersectionObserver: the bar is fixed
     * to the bottom edge and therefore always intersects, whether it is shown or not. Its
     * visibility is the `is-visible` class the theme's own element sets.
     *
     * No template guard here. The purchase rule above is product-template only because it
     * reasons about in-page buy controls; a sticky bar crowds this menu wherever it appears.
     */
    observeBuyBar() {
      if (typeof window.MutationObserver !== 'function') return;

      const buyBar = document.querySelector('product-quick-add');
      if (!buyBar) return;

      const sync = () => {
        this.setBuyEdge('bar', buyBar.classList.contains('is-visible'), buyBar);
      };

      if (this.buyBarObserver) this.buyBarObserver.disconnect();
      this.buyBarObserver = new window.MutationObserver(sync);
      this.buyBarObserver.observe(buyBar, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    /**
     * Full screen hero: its buy row sits on the bottom baseline, which is this corner. Same
     * conclusion as for the sticky bar, so the same class and the same behaviour, and this
     * time an IntersectionObserver is the right tool: the row scrolls with the page, so
     * whether it is in view is a real question here.
     *
     * At every width, not mobile only. On the landingpage the row is right aligned on
     * desktop too, which puts the button exactly under the toggle.
     */
    observeHeroBuy() {
      if (typeof window.IntersectionObserver !== 'function') return;

      const heroBuy = document.querySelector('[data-zh-plp-hero-buy]');
      if (!heroBuy) return;

      if (this.heroBuyObserver) this.heroBuyObserver.disconnect();
      this.heroBuyObserver = new window.IntersectionObserver((entries) => {
        this.setBuyEdge('hero', entries.some((entry) => entry.isIntersecting), heroBuy);
      });
      this.heroBuyObserver.observe(heroBuy);
    }

    /**
     * Two sources can claim the bottom edge, the sticky bar and the hero's buy row, and they
     * share one class. Combined here instead of each toggling it directly: the later observer
     * to fire would otherwise undo the other one's claim.
     *
     * The claim lifts the menu by the claimant's own height, so it clears the bar or the buy
     * row and stays reachable. Hiding it, the first version of this, left the landingpage
     * without a menu at all: the sticky bar comes up exactly when the hero's buy row goes out
     * of view, so one rule handed over to the other and the menu was never back.
     *
     * The height is measured rather than configured. The bar's height is a section setting, the
     * hero's buy row wraps at narrow widths, and a hard number would be wrong in both cases.
     */
    setBuyEdge(source, active, element) {
      if (!this.buyEdge) this.buyEdge = {};
      this.buyEdge[source] = active === true ? element || true : false;

      let lift = 0;
      Object.keys(this.buyEdge).forEach((key) => {
        const claim = this.buyEdge[key];
        if (!claim) return;
        const height = claim && claim.offsetHeight ? claim.offsetHeight : 0;
        if (height > lift) lift = height;
      });

      const claimed = Object.keys(this.buyEdge).some((key) => this.buyEdge[key]);
      if (claimed) this.root.style.setProperty('--zhsd-lift', lift + 12 + 'px');
      else this.root.style.removeProperty('--zhsd-lift');
      this.root.classList.toggle('zhsd--buybar-visible', claimed);
    }

    /** Handlers on our own markup. Bound once per element, see connectedCallback. */
    bindOwnNodes() {
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onListKeydown = this.onListKeydown.bind(this);
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onEditorSectionLoad = this.onEditorSectionLoad.bind(this);

      this.toggle.addEventListener('click', this.onToggleClick);
      this.trackMagnet(this.toggle, this.toggle);

      if (this.list) {
        this.list.addEventListener('keydown', this.onListKeydown);
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.close({ focusToggle: true }));
      }

      this.links.forEach((link) => {
        const item = link.closest('.zhsd__item');
        this.trackMagnet(link, item || link);
        link.addEventListener('click', (event) => this.onLinkClick(link, event));
      });
    }

    /**
     * Entrance. The dial is the last thing that arrives on a page load: the load animation plays
     * (sections/zh-preloader.liquid), then the hero cascade (assets/zh-home-motion.js), then this.
     *
     * Both hand over the same way, a pending flag plus one event, and both flip the flag before
     * dispatching. So the flag is what decides whether to wait at all: this script is deferred and
     * runs after zh-preloader.js, which on the "nothing to play" path has already dispatched, and
     * waiting for an event that is in the past would hide the dial forever. The hidden state itself
     * is CSS and carries its own keyframe failsafe, so a handover that never lands is covered too.
     */
    scheduleArrival() {
      const root = this.root;
      if (!root.classList.contains('zhsd--wait') || root.classList.contains('zhsd--in')) return;

      const delay = Number(this.dataset.enterDelay) || 0;
      const arrive = () => {
        window.setTimeout(() => root.classList.add('zhsd--in'), delay);
      };

      if (window.zhHomeIntro && window.zhHomeIntro.pending) {
        document.addEventListener('zh:intro:done', arrive, { once: true });
      } else if (window.zhPreloader && window.zhPreloader.pending) {
        // No hero cascade on this template, so the curtain is the last thing to wait for.
        document.addEventListener('zh:preloader:done', arrive, { once: true });
      } else {
        arrive();
      }
    }

    get isOpen() {
      return this.root.dataset.open === 'true';
    }

    setOpen(isOpen) {
      // Every way out of the open menu passes here, the toggle included, which is what makes
      // this the one place to drop a wait the visitor has stopped waiting for.
      if (!isOpen) this.cancelPendingAction();

      this.root.dataset.open = String(isOpen);
      this.toggle.setAttribute('aria-expanded', String(isOpen));

      const label = isOpen ? this.dataset.labelOpen : this.dataset.labelClosed;
      if (label) this.toggle.setAttribute('aria-label', label);

      if (this.list) {
        this.list.inert = !isOpen;
        this.list.setAttribute('aria-hidden', String(!isOpen));
      }

      this.links.forEach((link) => {
        link.tabIndex = isOpen ? 0 : -1;
      });

      // Document listeners only exist while the menu is open.
      if (isOpen) {
        if (this.closeOutside) document.addEventListener('pointerdown', this.onDocumentPointerDown);
        document.addEventListener('keydown', this.onDocumentKeydown);
      } else {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.resetMagnet(this.toggle);
        this.links.forEach((link) => this.resetMagnet(link.closest('.zhsd__item') || link));
      }
    }

    open() {
      this.setOpen(true);
    }

    close({ focusToggle = false } = {}) {
      if (!this.isOpen) return;
      this.setOpen(false);
      if (focusToggle) this.toggle.focus({ preventScroll: true });
    }

    onToggleClick(event) {
      this.ripple(this.toggle, event);
      this.setOpen(!this.isOpen);
    }

    onLinkClick(link, event) {
      this.ripple(link.querySelector('.zhsd__icon') || link, event);

      const type = link.dataset.zhsdAction;
      const value = link.dataset.zhsdValue;
      const labelNode = link.querySelector('.zhsd__label');
      const label = labelNode ? labelNode.textContent.trim() : '';
      const navigates = link.tagName === 'A' && link.getAttribute('href') && link.getAttribute('href') !== '#';

      if (type && value) {
        event.preventDefault();
        // An action whose widget has not landed yet is not done with, so the menu stays up and
        // the item spins. Closing here was the bug: the menu went away, nothing took its place,
        // and the panel turned up seconds later or not at all. settlePending() does the close.
        if (!this.runAction(type, value, label, link)) return;
      }

      if (this.closeAfterClick) {
        this.close({ focusToggle: !navigates || Boolean(type) });
      }
    }

    /**
     * Chat widgets and similar third parties are usually loaded with async, so the target of an
     * action can appear after the first click. Retry every ACTION_RETRY_STEP_MS for up to
     * ACTION_RETRY_LIMIT attempts before giving up.
     *
     * Returns whether the action actually fired, which is the only thing that closes the menu.
     * Everything else leaves it open: either the wait is still running and the item spins, or
     * the tap failed and the menu is where the next one lands.
     *
     * Only one retry chain runs at a time: a second click replaces the pending one, and closing
     * the menu, leaving the page or the theme editor re-rendering the section cancels it.
     */
    runAction(type, value, label, link, attempt = 0) {
      if (attempt === 0) this.cancelPendingAction();

      if (type === 'event') {
        window.dispatchEvent(new CustomEvent('zh:speed-dial', { detail: { action: value, label: label, source: this } }));
        return true;
      }

      if (type === 'click') {
        let element;
        try {
          element = document.querySelector(value);
        } catch (error) {
          // A typo in the setting, so no widget is ever going to turn up for it. Waiting on it
          // would spin for fifteen seconds over a theme-editor mistake.
          console.warn('[zh-speed-dial] invalid CSS selector:', value);
          this.settlePending(false);
          return false;
        }
        if (element) return this.fireAction(() => element.click(), value, attempt);
      }

      if (type === 'js') {
        const call = this.resolveJsAction(value);
        if (call) return this.fireAction(call, value, attempt);
      }

      if (attempt < ACTION_RETRY_LIMIT) {
        if (attempt === 0) this.beginPending(link);
        this.actionTimer = window.setTimeout(
          () => this.runAction(type, value, label, link, attempt + 1),
          ACTION_RETRY_STEP_MS
        );
        return false;
      }

      console.warn('[zh-speed-dial] action target never showed up:', type, value);
      this.settlePending(false);
      return false;
    }

    /**
     * Resolves a dotted path such as "ResolviaChat.open" against window, tolerating a trailing ()
     * in the setting. Returns a callable that keeps the path's own object as `this`, or null while
     * the widget has not defined it yet.
     */
    resolveJsAction(value) {
      const path = value.replace(/\(\)\s*$/, '').split('.');
      let scope = window;
      let fn = window;

      for (const step of path) {
        if (fn == null) break;
        scope = fn;
        fn = fn[step];
      }

      return typeof fn === 'function' ? () => fn.call(scope) : null;
    }

    /**
     * Runs a resolved action. A widget that is there but throws ends the wait instead of spinning
     * on: it will throw again on the next attempt, and an item left spinning with aria-busy for
     * the rest of the page is the exact state this path exists to prevent.
     *
     * Only a synchronous throw is caught. A rejected promise from an async widget API stays
     * unhandled on purpose, so error tracking still sees a third party failing.
     */
    fireAction(action, value, attempt) {
      try {
        action();
      } catch (error) {
        console.warn('[zh-speed-dial] action threw:', value, error);
        this.settlePending(false);
        return false;
      }

      if (attempt > 0) this.settlePending(true);
      return true;
    }

    /**
     * Marks the clicked item as waiting. The spinner is CSS and aria-busy is a property neither
     * of which a screen reader announces on its own, so the live region carries the actual news.
     */
    beginPending(link) {
      if (!link) return;
      this.pendingLink = link;
      link.classList.add('zhsd__link--pending');
      link.setAttribute('aria-busy', 'true');
      this.announce(this.dataset.labelPending);
    }

    /**
     * Ends the wait. A widget that answered gets the close the click itself skipped; one that
     * never came leaves the menu open, so the next tap has somewhere to land, and says so rather
     * than letting the "loading" announcement stand as the last thing anyone heard.
     */
    settlePending(answered) {
      this.clearPendingMark();

      if (!answered) {
        this.announce(this.dataset.labelFailed);
        return;
      }

      if (!this.closeAfterClick) return;

      // Take focus back only while it is still ours to take. By now the widget may have opened
      // its own panel and focused it, and pulling focus out of that is worse than leaving the
      // menu's trigger unfocused.
      const stillOurs = !document.activeElement || this.contains(document.activeElement);
      this.close({ focusToggle: stillOurs });
    }

    clearPendingMark() {
      const link = this.pendingLink;
      this.pendingLink = null;
      this.announce('');
      if (!link) return;
      link.classList.remove('zhsd__link--pending');
      link.removeAttribute('aria-busy');
    }

    /** Live region, rendered empty so it is in the DOM before there is anything to say. */
    announce(text) {
      if (!this.status) return;
      this.status.textContent = text || '';
    }

    cancelPendingAction() {
      this.clearPendingMark();
      if (!this.actionTimer) return;
      window.clearTimeout(this.actionTimer);
      this.actionTimer = null;
    }

    onDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close({ focusToggle: true });
      }
    }

    /** Roving focus inside the open stack; item 1 sits closest to the trigger button. */
    onListKeydown(event) {
      const keys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      const current = this.links.indexOf(document.activeElement);
      if (current === -1) return;

      let next = current;
      if (event.key === 'ArrowUp') next = current + 1;
      if (event.key === 'ArrowDown') next = current - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.links.length - 1;

      if (next < 0 || next >= this.links.length) return;

      event.preventDefault();
      this.links[next].focus({ preventScroll: true });
    }

    onEditorSelect(event) {
      if (!this.contains(event.target) && event.target !== this) return;
      this.open();
    }

    /** Slight lean towards the cursor; skipped for touch and reduced motion. */
    trackMagnet(trigger, target) {
      if (!this.useMagnet) return;

      trigger.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch' || this.reduceMotion.matches) return;

        const bounds = trigger.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;

        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;
        target.style.setProperty('--pull-x', `${(x * 7).toFixed(2)}px`);
        target.style.setProperty('--pull-y', `${(y * 7).toFixed(2)}px`);
      });

      trigger.addEventListener('pointerleave', () => this.resetMagnet(target));
    }

    resetMagnet(target) {
      if (!target) return;
      target.style.setProperty('--pull-x', '0px');
      target.style.setProperty('--pull-y', '0px');
    }

    ripple(host, event) {
      if (!this.useRipple || this.reduceMotion.matches || !host) return;

      const bounds = host.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'zhsd__ripple';
      ripple.style.left = `${(event.clientX || bounds.left + bounds.width / 2) - bounds.left}px`;
      ripple.style.top = `${(event.clientY || bounds.top + bounds.height / 2) - bounds.top}px`;
      host.append(ripple);
      ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
    }
  }

  customElements.define('zh-speed-dial', ZhSpeedDial);
}
